import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { addDays } from 'date-fns';
import { useEffect, useEffectEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BokehBackground } from '@sorare/core/src/atoms/animations/BokehBackground';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandL,
  BrandXS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { TimeLabel } from '@sorare/core/src/components/labels/TimeLabel/index.tsx';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount.ts';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { BundledPurchasableItem } from '#src/components/commonCard/BuyPacks/GemsPack/BundledPurchasableItem/index.tsx';
import { VoucherPaymentDialog } from '#src/components/commonCard/BuyPacks/GemsPack/VoucherPaymentDialog/index.tsx';
import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  WelcomePackDialogQuery,
  WelcomePackDialogQueryVariables,
  WelcomePackPurchasedOfferQuery,
  WelcomePackPurchasedOfferQueryVariables,
} from './__generated__/index.graphql.ts';

const StyledDialod = styled(Dialog)`
  box-shadow: 0 0 30px var(--c-set-surface-primary);
  border: none;
`;
const DialogBody = styled(Vertical)`
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  padding: var(--double-unit);
  min-height: 100%;
`;
const CloseButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: var(--triple-unit);
  flex: 1;
  justify-content: space-between;
`;

const Header = styled(Vertical)`
  justify-content: flex-start;
`;

const BundleSection = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: var(--double-unit);
  margin: auto;
  max-width: 100%;
  > * {
    width: calc(100% / 3);
  }
`;

const Label = styled.div`
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translate(-50%, 50%);
  text-transform: uppercase;
`;

const Footer = styled(Vertical).attrs({ gap: 0.5, center: true })`
  padding: 0 var(--double-unit) var(--quadruple-unit);
`;

const ButtonWrapper = styled(ButtonBase)`
  position: relative;
  display: inline-block;
`;

const DiscountBadgeWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  transform: translateY(50%);
  right: 0;
  z-index: 1;
`;

const labels = [
  {
    label: <FormattedMessage defaultMessage="Advanced" />,
    color: 'common' as const,
  },
  {
    label: <FormattedMessage defaultMessage="Elite" />,
    color: 'glow' as const,
  },
  {
    label: <FormattedMessage defaultMessage="Legendary" />,
    color: 'fire' as const,
  },
];

const WELCOME_PACK_DIALOG_QUERY = gql`
  query WelcomePackDialogQuery($sport: Sport!) {
    currentUser {
      slug
      availableVoucher(sport: $sport) {
        id
        displayName
        endDate
        purchasableItems {
          slug
          ... on BundledPurchasableItem {
            slug
            ...BundledPurchasableItem_bundledPurchasableItem
          }
        }
        buyAllPurchasableItem {
          slug
          id
          price {
            ...useMonetaryAmount_monetaryAmount
          }
          ... on BundledPurchasableItem {
            slug
            discountBasisPoints
          }
        }
      }
    }
    tokens {
      purchasableItems(sport: $sport) {
        slug
        ... on BundledPurchasableItem {
          slug
          ...BundledPurchasableItem_bundledPurchasableItem
        }
      }
    }
  }
  ${BundledPurchasableItem.fragments.bundledPurchasableItem}
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<WelcomePackDialogQuery, WelcomePackDialogQueryVariables>;

const WELCOME_PACK_PURCHASED_OFFER_QUERY = gql`
  query WelcomePackPurchasedOfferQuery($offerId: String!) {
    tokens {
      primaryOffer(id: $offerId) {
        id
        purchasedItems {
          slug
          ... on PackConfigPurchasableItem {
            slug
            pack {
              id
              cards {
                slug
                ...RevealPackCardsDialog_anyCardInterface
              }
            }
            cardPack {
              slug
              pictureUrl
              primaryColor: color(variant: PRIMARY)
              secondaryColor: color(variant: SECONDARY)
              cardSet {
                slug
              }
            }
          }
        }
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<
  WelcomePackPurchasedOfferQuery,
  WelcomePackPurchasedOfferQueryVariables
>;

const BOKEH_COLORS = ['#e1bc45', '#d4a843', '#ffffff'];

type Props = {
  onClose?: () => void;
};

export const WelcomePackDialog = ({ onClose: onCloseProps }: Props) => {
  const {
    flags: { useWelcomePromoPack = false },
  } = useFeatureFlags();
  const closeDialog = useCloseDialog();
  const { postMessage, isIosApp } = useIsMobileApp();

  const onClose = () => {
    if (isIosApp) {
      postMessage('closeCurrentWebview');
    } else {
      closeDialog(DialogKey.welcomePack);
      onCloseProps?.();
    }
  };
  const { toMonetaryAmount } = useMonetaryAmount();
  const { sport } = useSportContext();
  const { data } = useQuery(WELCOME_PACK_DIALOG_QUERY, {
    variables: { sport },
    skip: !useWelcomePromoPack,
  });

  const [currentPackIndex, setCurrentPackIndex] = useState(0);
  const [buyAllDialogOpened, setBuyAllDialogOpened] = useState(false);

  const [fetch, { data: purchasedOfferData }] = useLazyQuery(
    WELCOME_PACK_PURCHASED_OFFER_QUERY
  );

  const purchasedPacks =
    purchasedOfferData?.tokens?.primaryOffer?.purchasedItems?.filter(item =>
      isType(item, 'PackConfigPurchasableItem')
    ) ?? [];

  const currentPack =
    purchasedPacks[currentPackIndex] &&
    isType(purchasedPacks[currentPackIndex], 'PackConfigPurchasableItem')
      ? purchasedPacks[currentPackIndex]
      : null;

  const revealDialogOpen = !!currentPack;

  const handleCloseRevealDialog = async () => {
    if (currentPackIndex < purchasedPacks.length - 1) {
      setCurrentPackIndex(prev => prev + 1);
    } else {
      setCurrentPackIndex(0);
      onClose();
    }
  };

  const handlePackPurchased = (offerId: string) => {
    fetch({
      variables: { offerId: idFromObject(offerId!) },
    });
  };

  const { currentUser } = data || {};
  const bundledItems =
    currentUser?.availableVoucher?.purchasableItems.filter(item =>
      isType(item, 'BundledPurchasableItem')
    ) || [];
  const buyAllPurchasableItem =
    currentUser?.availableVoucher?.buyAllPurchasableItem;
  const buyAllPrice = toMonetaryAmount(buyAllPurchasableItem?.price);
  const discountPercent =
    isType(buyAllPurchasableItem, 'BundledPurchasableItem') &&
    buyAllPurchasableItem.discountBasisPoints
      ? Math.round(buyAllPurchasableItem.discountBasisPoints / 100)
      : null;
  const { main: buyAllPriceLabel } = useAmountWithConversion({
    monetaryAmount: buyAllPrice,
  });
  const voucherId = currentUser?.availableVoucher?.id;
  const endDate = currentUser?.availableVoucher?.endDate;

  const { lifecycle, update } = useLifecycle();

  const recordAutoOpenShown = useEffectEvent(() => {
    if (!voucherId) return;
    update(LIFECYCLE.welcomePackAutoOpen, {
      ...lifecycle?.[LIFECYCLE.welcomePackAutoOpen],
      [voucherId]: addDays(Date.now(), 2),
    });
  });

  useEffect(() => {
    recordAutoOpenShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  if (!useWelcomePromoPack || !voucherId) return null;

  return (
    <>
      <StyledDialod
        maxWidth="xs"
        open
        onClose={onClose}
        blurBackdrop
        hideHeader
      >
        {({ CloseButton }) => {
          return (
            <DialogBody>
              <BokehBackground
                colors={BOKEH_COLORS}
                speed={0.1}
                baseRadius={0.01}
                endRadius={0.1}
                density={2}
                blur={0.9}
              />
              <CloseButtonWrapper>
                <CloseButton onClose={onClose} transparent />
              </CloseButtonWrapper>
              <Content>
                <Header gap={3}>
                  <Vertical center>
                    {!!endDate && (
                      <TimeLabel state="countdown">
                        <TimeLeft time={endDate} />
                      </TimeLabel>
                    )}
                    <BrandL className="text-center">
                      <FormattedMessage defaultMessage="Claim your exclusive offer!" />
                    </BrandL>
                  </Vertical>
                  <BodyS className="text-center">
                    <FormattedMessage
                      defaultMessage="Select an offer or unlock them all.{br}Remaining offers expire upon purchase."
                      values={{ br: <br /> }}
                    />
                  </BodyS>
                </Header>

                <BundleSection>
                  {bundledItems.map((bundledItem, index) => (
                    <BundledPurchasableItem
                      key={bundledItem.id}
                      bundledPurchasableItem={bundledItem}
                      onPackPurchased={handlePackPurchased}
                      voucherId={voucherId}
                    >
                      <Label>
                        <Chip
                          key={bundledItem.id}
                          size="small"
                          brand
                          color={labels[index].color}
                        >
                          {labels[index].label}
                        </Chip>
                      </Label>
                    </BundledPurchasableItem>
                  ))}
                </BundleSection>
                <ButtonWrapper onClick={() => setBuyAllDialogOpened(true)}>
                  {!!discountPercent && (
                    <DiscountBadgeWrapper>
                      <CornerBadge color="linear-gradient(188deg, rgb(255, 255, 255) 1.37%, rgb(252, 255, 99) 36.66%, rgb(194, 105, 0) 101.57%)">
                        <BrandXS>
                          <FormattedMessage
                            defaultMessage="-{percent, number, ::percent} discount**"
                            values={{ percent: discountPercent / 100 }}
                          />
                        </BrandXS>
                      </CornerBadge>
                    </DiscountBadgeWrapper>
                  )}
                  <Button size="medium" asSpan>
                    <FormattedMessage
                      defaultMessage="Get all for {price}"
                      values={{ price: buyAllPriceLabel }}
                    />
                  </Button>
                </ButtonWrapper>

                <Footer>
                  <LabelS color="var(--c-nd-600)">
                    <FormattedMessage defaultMessage="*Each exclusive deal can only be claimed once. After your purchase, all remaining offers will expire." />
                  </LabelS>
                  <LabelS color="var(--c-nd-600)">
                    <FormattedMessage defaultMessage="**Global discount applied to the total combined reference prices of the three offers." />
                  </LabelS>
                </Footer>
              </Content>
            </DialogBody>
          );
        }}
      </StyledDialod>
      {buyAllPurchasableItem ? (
        <VoucherPaymentDialog
          open={buyAllDialogOpened}
          onClose={() => setBuyAllDialogOpened(false)}
          purchasableItemId={buyAllPurchasableItem.id}
          voucherId={voucherId}
          price={buyAllPrice}
          onPackPurchased={handlePackPurchased}
        />
      ) : null}
      {revealDialogOpen ? (
        <RevealPackCardsDialog
          key={currentPack.pack?.id}
          open
          onClose={handleCloseRevealDialog}
          cards={currentPack.pack?.cards ?? []}
          onContinue={handleCloseRevealDialog}
          packId={currentPack.pack?.id ?? ''}
          packPictureUrl={currentPack.cardPack?.pictureUrl || ''}
          primaryColor={ensureHexColor(
            currentPack.cardPack?.primaryColor || ''
          )}
          secondaryColor={ensureHexColor(
            currentPack.cardPack?.secondaryColor || ''
          )}
          cardSetSlug={currentPack.cardPack?.cardSet?.slug || ''}
          showPlayerDetails
        />
      ) : null}
    </>
  );
};
